import React, { FC, useCallback, useState } from 'react';
import { Modal, ModalHeader } from 'reactstrap';
import {
  AddressDistanceCriterionOptions,
  BaseCriterionOptions,
  CompositeCriterionOptions,
  PointsCriterion,
  PreviousYearAttendanceCriterionOptions,
  ValidCriterionName,
  ValidCriterions,
  ValueCriterionOptions,
} from '../../../../shared/orders/raffle';
import { useTranslation } from '../../../translations';
import {
  AddressDistance,
  MathOperation,
  PrevYearsAttended,
  RandomNumber,
  SelectList,
  StaticValue,
} from './components';
import { PointsValue } from './components/pointsValue';
import {
  componentList,
  componentNameToId,
  criterionToComponentName,
  RaffleConfiguratorProps,
} from './utils';

interface Props {
  readonly criterion?: ValidCriterions;
  readonly selectedType?: ValidCriterionName;
  readonly onFinish: (criterion: ValidCriterions) => void;
  readonly onCancel: () => void;
}

export const ConfigureRaffleNodeModal: FC<Props> = ({
  criterion,
  selectedType,
  onCancel,
  onFinish,
}) => {
  const { ts } = useTranslation();
  const [selectedComponentId, setSelectedComponentId] = useState<number | null>(
    selectedType
      ? componentList.findIndex(
          ({ componentName }) => componentName === criterionToComponentName[selectedType],
        )
      : null,
  );

  const [showComponentSelect, setShowComponentSelect] = useState(!selectedComponentId);
  const [displayName, setDisplayName] = useState<string | undefined>(criterion?.displayName);
  const onComponentSelected = useCallback((id: number) => {
    setSelectedComponentId(id);
    setShowComponentSelect(false);
  }, []);

  const onFinishFn = useCallback(
    (opts: ValidCriterions) => {
      if (criterion && opts.name === 'composite' && opts.name === criterion.name) {
        opts.options.composites = criterion.options.composites;
      }

      onFinish({ displayName, ...opts });
    },
    [criterion, displayName, onFinish],
  );

  return (
    <Modal isOpen>
      <ModalHeader>
        {ts(criterion ? 'common.update' : 'add_new')} {ts('raffle_component')}
      </ModalHeader>
      {showComponentSelect ? (
        <SelectList
          defaultComponentId={selectedComponentId ?? componentNameToId(criterion?.name)}
          name={displayName}
          onCancel={onCancel}
          onNameChange={setDisplayName}
          onNextClicked={onComponentSelected}
        />
      ) : (
        getComponent({
          defaultOptions: criterion?.options,
          displayName,
          id: selectedComponentId!,
          onBack: () => {
            setShowComponentSelect(true);
          },
          onCancel,
          onConfigure: onFinishFn,
        })
      )}
    </Modal>
  );
};

interface RenderComponentProps {
  defaultOptions?: BaseCriterionOptions;
  displayName?: string;
  id: number;
  readonly onBack: () => void;
  readonly onCancel: () => void;
  readonly onConfigure: (opts: ValidCriterions) => void;
}

function getComponent({
  id,
  defaultOptions,
  ...props
}: RenderComponentProps): React.JSX.Element | null {
  switch (componentList[id]?.componentName) {
    case 'MathOperation': {
      return (
        <MathOperation
          defaultOptions={defaultOptions as CompositeCriterionOptions}
          {...(props as RaffleConfiguratorProps<CompositeCriterionOptions>)}
        />
      );
    }

    case 'RandomNumber': {
      return <RandomNumber {...(props as RaffleConfiguratorProps)} />;
    }

    case 'AddressDistance': {
      return (
        <AddressDistance
          defaultOptions={defaultOptions as AddressDistanceCriterionOptions}
          {...(props as RaffleConfiguratorProps<AddressDistanceCriterionOptions>)}
        />
      );
    }

    case 'StaticValue': {
      return (
        <StaticValue
          defaultOptions={defaultOptions as ValueCriterionOptions}
          {...(props as RaffleConfiguratorProps<ValueCriterionOptions>)}
        />
      );
    }

    case 'PrevYearsAttendance': {
      return (
        <PrevYearsAttended
          defaultOptions={defaultOptions as PreviousYearAttendanceCriterionOptions}
          {...(props as RaffleConfiguratorProps<PreviousYearAttendanceCriterionOptions>)}
        />
      );
    }

    case 'PointsValue': {
      return <PointsValue {...(props as RaffleConfiguratorProps<PointsCriterion>)} />;
    }

    default: {
      return null;
    }
  }
}
